import { useMemo } from "react";
import { StopCircleIcon, CheckCircleIcon, XCircleIcon, ForwardIcon } from "../../../automationStudioIcons";
import ButtonComponent from "../../../../customButton";
import { EXECUTION_STATUS } from '../../../../AutomationStudio/hooks/useReplayExecution';
import AutomationText from '../../shared/AutomationText';
import styles from "../../../../../styles/automationStudio/automationStudio.module.scss";
import { LABELS } from "../../../content";
import { formatDuration, getTotalSteps } from '../../../../AutomationStudio/utils';
import { extractAutomationScreenshotPath } from "../../../../../utils/screenshotUtils";
import {
    mergeExecDetailIntoEvaluationStep,
    getEffectiveStepStatus,
    getReplayStepDisplayDescription,
    getEvaluationNarrativeFromExecDetail,
    getEvaluationNarrativeFromPlanStep,
} from '../../../../AutomationStudio/utils/stepExecutionDisplay';
import useAutomationTextFormatter from '../../../../AutomationStudio/hooks/useAutomationTextFormatter';
import AIEvaluationBlock from "../../shared/AIEvaluationBlock";
import ScreenshotPreview from "../../shared/ScreenshotPreview";

/**
 * RunningExecutionView - Live execution monitoring
 * Shows current step progress, completed steps, and live screenshot
 * Uses replayProgressMap for step status during replay
 *
 * Generate AI: active step comes from `instructions` (not flat replay plan); description must not use
 * stale `replayExecutionResults.stepDetails` from a prior replay — only pass execDetail when `replayExecuting`.
 */
export default function RunningExecutionView({
    currentStep,
    instructions = [],
    executionResults: _executionResults = null,
    replayExecutionResults = null,
    replayProgressMap = {},
    replayExecuting = false,
    replayCurrentStepIndex = null,
    flatStepsForReplay = [],
    onStopExecution
}) {
    const { format } = useAutomationTextFormatter();

    // Calculate execution progress
    const totalSteps = flatStepsForReplay?.length > 0
        ? flatStepsForReplay.length
        : getTotalSteps(instructions);
    // During replay, count completed from replayProgressMap; else from step.status
    const completedSteps = replayExecuting && replayProgressMap
        ? Object.values(replayProgressMap).filter(s =>
            s === EXECUTION_STATUS.PASSED ||
            s === EXECUTION_STATUS.FAILED ||
            s === EXECUTION_STATUS.SKIPPED
        ).length
        : instructions.reduce((sum, inst) => {
            if (!inst.aiSteps) return sum;
            return sum + inst.aiSteps.filter((s) => {
                const status = s.status;
                return (
                    status === 'success' ||
                    status === 'failed' ||
                    status === 'completed' ||
                    status === 'failed_by_evaluation'
                );
            }).length;
        }, 0);
    const progressPercent = totalSteps > 0 ? (completedSteps / totalSteps) * 100 : 0;
    const showStepProgressBar = totalSteps > 0;

    // Derive current step index
    // - During replay prefer replayCurrentStepIndex when it is a valid (>= 0) index
    // - Otherwise, try to locate currentStep within the flattened steps (non-replay "Generate AI" flow)
    // - As a final fallback, use completedSteps (never show step 0 when there are steps)
    const hasValidReplayIndex = replayCurrentStepIndex != null && replayCurrentStepIndex >= 0;

    let effectiveStepIndex = null;

    if (replayExecuting && hasValidReplayIndex) {
        // Replay flow: index comes directly from replayCurrentStepIndex
        effectiveStepIndex = replayCurrentStepIndex;
    } else if (!replayExecuting && currentStep?.id && Array.isArray(instructions) && instructions.length > 0) {
        // Generate AI flow: derive index from flattened steps in display order
        const allSteps = [];
        instructions.forEach(inst => {
            (inst.aiSteps || []).forEach(step => {
                allSteps.push(step);
            });
        });
        const foundIndex = allSteps.findIndex(step => step.id === currentStep.id);
        if (foundIndex !== -1) {
            effectiveStepIndex = foundIndex;
        }
    }

    if (effectiveStepIndex == null && totalSteps > 0) {
        // Fallback: use completedSteps but clamp within [0, totalSteps - 1]
        const inferredIndex = Math.min(
            Math.max(completedSteps, 0),
            Math.max(totalSteps - 1, 0)
        );
        effectiveStepIndex = inferredIndex;
    }

    // 1-based step for header progress ("Executing Step N of M") — always the in-progress replay index or Generate AI cursor
    const currentStepNum = effectiveStepIndex != null ? effectiveStepIndex + 1 : null;

    /** During replay, preview card shows the latest finished step (Passed/Failed/Skipped), not the one currently executing. */
    const lastExecutedReplayStepIndex = useMemo(() => {
        if (!replayExecuting || !replayProgressMap || typeof replayProgressMap !== 'object') return null;
        let best = -1;
        Object.entries(replayProgressMap).forEach(([key, status]) => {
            if (
                status !== EXECUTION_STATUS.PASSED &&
                status !== EXECUTION_STATUS.FAILED &&
                status !== EXECUTION_STATUS.SKIPPED
            ) {
                return;
            }
            const idx = Number(key);
            if (Number.isFinite(idx) && idx > best) best = idx;
        });
        return best >= 0 ? best : null;
    }, [replayExecuting, replayProgressMap]);

    const previewStepIndex = replayExecuting ? lastExecutedReplayStepIndex : effectiveStepIndex;

    // Retrieve step shown in the preview card (last completed during replay)
    let activeStepObj = null;
    if (replayExecuting && flatStepsForReplay?.length > 0 && previewStepIndex != null) {
        activeStepObj = flatStepsForReplay[previewStepIndex];
    } else if (!replayExecuting && previewStepIndex != null && instructions?.length > 0) {
        const allSteps = [];
        instructions.forEach(inst => {
            (inst.aiSteps || []).forEach(step => {
                allSteps.push(step);
            });
        });
        activeStepObj = allSteps[previewStepIndex];
    }

    const execDetail = replayExecutionResults?.stepDetails?.[previewStepIndex];
    const confidence = execDetail?.result?.result?.confidence ?? execDetail?.result?.details?.confidence ?? null;
    // Replay: prefer WebSocket stepDetails. Generate AI: prefer live plan step — stale replay stepDetails
    // at the same index must not override the current step's screenshot (and object-shaped fields need normalization).
    const rawScreenshot = replayExecuting
        ? extractAutomationScreenshotPath(execDetail, activeStepObj)
        : (
            extractAutomationScreenshotPath(null, activeStepObj) ??
            extractAutomationScreenshotPath(execDetail, activeStepObj)
        );

    const mergedEvalStep = useMemo(
        () => mergeExecDetailIntoEvaluationStep(activeStepObj, execDetail),
        [activeStepObj, execDetail]
    );

    const stepDisplayDescription = useMemo(
        () => getReplayStepDisplayDescription(
            activeStepObj,
            replayExecuting ? execDetail : null
        ),
        [activeStepObj, execDetail, replayExecuting]
    );

    const resolvedStepDisplayDescription = useMemo(
        () => format(stepDisplayDescription),
        [stepDisplayDescription, format]
    );

    const effectiveStatus = replayExecuting && replayProgressMap && typeof previewStepIndex === 'number'
        ? replayProgressMap[previewStepIndex]
        : activeStepObj?.status;

    const isEvaluationStep = activeStepObj?.tool === 'evaluateTestStep' || execDetail?.tool === 'evaluateTestStep';

    let statusText = "Pending";
    if (isEvaluationStep && mergedEvalStep) {
        if (effectiveStatus === 'running' || effectiveStatus === EXECUTION_STATUS.IN_PROGRESS) {
            statusText = "Running";
        } else if (effectiveStatus === EXECUTION_STATUS.SKIPPED) {
            statusText = "Skipped";
        } else {
            const st = getEffectiveStepStatus(mergedEvalStep, execDetail);
            if (st === 'failed') statusText = "Failed";
            else if (st === 'success') statusText = "Passed";
            else if (effectiveStatus === 'failed' || effectiveStatus === EXECUTION_STATUS.FAILED) statusText = "Failed";
            else if (effectiveStatus === 'success' || effectiveStatus === EXECUTION_STATUS.PASSED) statusText = "Passed";
        }
    } else if (effectiveStatus === 'success' || effectiveStatus === EXECUTION_STATUS.PASSED) {
        statusText = "Passed";
    } else if (effectiveStatus === 'failed' || effectiveStatus === EXECUTION_STATUS.FAILED) {
        statusText = "Failed";
    } else if (effectiveStatus === 'running' || effectiveStatus === EXECUTION_STATUS.IN_PROGRESS) {
        statusText = "Running";
    } else if (effectiveStatus === EXECUTION_STATUS.SKIPPED) {
        statusText = "Skipped";
    }

    const stepDuration = replayExecuting
        ? execDetail?.duration ?? execDetail?.result?.duration ?? execDetail?.result?.result?.duration
        : execDetail?.duration ?? execDetail?.result?.duration ?? execDetail?.result?.result?.duration ?? activeStepObj?.duration;

    // evaluateTestStep: narrative comes from the same fields as StepEditorView (replayEval) / VerificationTab.
    // Replay: WebSocket stepDetails only; Generate AI: execDetail + plan step fallbacks.
    const reason = replayExecuting
        ? getEvaluationNarrativeFromExecDetail(execDetail)
        : (
            getEvaluationNarrativeFromExecDetail(execDetail) ??
            getEvaluationNarrativeFromPlanStep(activeStepObj)
        );

    const reasonDisplay = useMemo(() => format(reason), [reason, format]);

    return (
        <div className={styles.runningExecutionContainer}>
            {/* Header with Progress */}
            <div className={styles.runningExecutionHeader}>
                <div className={styles.progressSection}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span className={`${styles.asSpinner} ${styles.asSpinnerSm}`} style={{ borderTopColor: '#2563EB' }} aria-hidden role="presentation" />
                        <h2 className={`${styles.runningTitle} ${styles.asTypoSubtitle2}`}>
                            {LABELS.EXECUTING_AUTOMATION}
                        </h2>
                    </div>
                    <p className={`${styles.progressText} ${styles.asTypoCaption}`}>
                        {currentStepNum != null && currentStepNum >= 1
                            ? `Executing Step ${currentStepNum}${totalSteps > 0 ? ` of ${totalSteps}` : ''}`
                            : 'Starting automation…'}
                    </p>
                    {showStepProgressBar && (
                        <div
                            className={styles.progressBar}
                            role="progressbar"
                            aria-valuenow={Math.round(progressPercent)}
                            aria-valuemin={0}
                            aria-valuemax={100}
                            style={{ overflow: 'hidden' }}
                        >
                            <div className={styles.asLinearProgressBar} style={{ width: `${progressPercent}%` }} />
                        </div>
                    )}
                </div>

                <ButtonComponent
                    ctaText={LABELS.STOP_EXECUTION}
                    variant="outline"
                    size="small"
                    className={`red600 ${styles.stopButton}`}
                    startIcon={<StopCircleIcon className="icon red600" />}
                    onClick={onStopExecution}
                />
            </div>

            {/* Current Executing Step Preview */}
            <div className={styles.stepsListSection}>
                <span className={`${styles.sectionLabel} ${styles.asTypoSubtitle2}`}>
                    {replayExecuting ? LABELS.LAST_EXECUTED_STEP : LABELS.CURRENTLY_EXECUTING_STEP}
                </span>
                {activeStepObj ? (
                    <div className={styles.activeStepPreviewCard}>
                        <div className={styles.activeStepInfoLeft}>
                            <div className={styles.activeStepPreviewHeader}>
                                <div className={styles.activeStepPreviewIcon}>
                                    {statusText === 'Passed' ? <CheckCircleIcon className={styles.stepIconSuccess} /> :
                                     statusText === 'Failed' ? <XCircleIcon className={styles.stepIconFailed} /> :
                                     statusText === 'Running' ? <span className={`${styles.asSpinner} ${styles.asSpinnerSm}`} aria-hidden role="presentation" /> :
                                     statusText === 'Skipped' ? <ForwardIcon className={styles.stepIconSkipped} /> :
                                     <div className={styles.stepIconPending} />}
                                </div>
                                <span className={`${styles.activeStepPreviewDesc} ${styles.asTypoBody2}`}>
                                    {resolvedStepDisplayDescription}
                                </span>
                            </div>

                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', padding: '0 4px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', backgroundColor: '#F1F5F9', padding: '4px 10px', borderRadius: '4px', border: '1px solid #E2E8F0' }}>
                                    <span className={`${styles.asTypoCaption}`} style={{ color: '#64748B' }}>Status:</span>
                                    <span className={`${styles.asTypoCaption}`} style={{ fontWeight: 600, color: statusText === 'Passed' ? '#16A34A' : statusText === 'Failed' ? '#DC2626' : statusText === 'Running' ? '#2563EB' : '#475569' }}>
                                        {statusText}
                                    </span>
                                </div>

                                {stepDuration != null && statusText !== 'Running' && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', backgroundColor: '#F1F5F9', padding: '4px 10px', borderRadius: '4px', border: '1px solid #E2E8F0' }}>
                                        <span className={`${styles.asTypoCaption}`} style={{ color: '#64748B' }}>Duration:</span>
                                        <span className={`${styles.asTypoCaption}`} style={{ fontWeight: 500, color: '#334155' }}>{formatDuration(stepDuration)}</span>
                                    </div>
                                )}

                                {activeStepObj?.tool && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', backgroundColor: '#F1F5F9', padding: '4px 10px', borderRadius: '4px', border: '1px solid #E2E8F0' }}>
                                        <span className={`${styles.asTypoCaption}`} style={{ color: '#64748B' }}>Tool:</span>
                                        <span className={`${styles.asTypoCaption}`} style={{ fontWeight: 500, color: '#334155' }}>{activeStepObj.tool}</span>
                                    </div>
                                )}

                                {confidence != null && statusText === 'Passed' && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', backgroundColor: '#F1F5F9', padding: '4px 10px', borderRadius: '4px', border: '1px solid #E2E8F0' }}>
                                        <span className={`${styles.asTypoCaption}`} style={{ color: '#64748B' }}>AI Confidence:</span>
                                        <span className={`${styles.asTypoCaption}`} style={{ fontWeight: 500, color: '#334155' }}>{Math.round(confidence <= 1 ? confidence * 100 : confidence)}%</span>
                                    </div>
                                )}
                            </div>

                            {isEvaluationStep && reasonDisplay && (
                                <AIEvaluationBlock
                                    reason={reasonDisplay}
                                    statusText={statusText}
                                    confidence={confidence}
                                    stepDuration={stepDuration}
                                />
                            )}
                        </div>

                        <div className={styles.activeStepScreenshotRight}>
                            <ScreenshotPreview
                                screenshotRaw={rawScreenshot}
                                emptyVariant={statusText === 'Running' ? 'waiting' : 'empty'}
                                fileName={
                                    typeof resolvedStepDisplayDescription === 'string'
                                        ? resolvedStepDisplayDescription
                                        : 'Step execution preview'
                                }
                            />
                        </div>
                    </div>
                ) : (
                    <div className={styles.compactStepsList} style={{ backgroundColor: '#F8FAFC', padding: 16, borderRadius: 8, border: '1px solid #E2E8F0', alignItems: 'center', justifyContent: 'center' }}>
                        <span className={`${styles.asTypoBody2}`} style={{ color: '#64748B' }}>
                            {replayExecuting ? LABELS.LAST_EXECUTED_STEP_WAITING : 'Waiting for step execution to begin...'}
                        </span>
                    </div>
                )}
            </div>

        </div>
    );
}
